import { useEffect, useReducer, useState } from "react";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../../context/bulkDeleteReducer";
import BoxWrapper from "../../../components/ui/BoxWrapper";
import { Link, useParams, useNavigate } from "react-router-dom";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import { colors } from "../../../config/theme";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { CSpinner } from "@coreui/react";
import RenderTable from "../../../components/ui/RenderTable";
import { general } from "../../../locales/general";
import Pagination from "../../../components/ui/Pagination";
import { useMutation, useQuery, useQueryClient } from "react-query";
import apiService from "../../../service/apiService";
import { useCookie } from "../../../hooks/useCookie";
import { useToast } from "../../../hooks/useToast";
import PlaceInspectorsTable from "./PlaceInspectorsTable";
import { useDebounce } from "../../../hooks/useDebounce";
import { toQuery } from "../../../helpers/general";
import { useSelectedPlace } from "../../../hooks/place/useSelectedPlace";
import { useAuth } from "../../../hooks/useAuth";
import PlaceSVG from "../../../components/icons/PlaceIcon";

const PAGE_SIZE = 10;

export default function PlaceInspectors() {
  const params = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const query = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (
      selectedPlaceId &&
      params.placeId &&
      params.placeId !== selectedPlaceId
    ) {
      navigate(`/places/${selectedPlaceId}/inspectors`, { replace: true });
    }
  }, [selectedPlaceId, params.placeId, navigate]);

  const placeId = selectedPlaceId ?? params.placeId;

  useEffect(() => {
    setCurrentPage(1);
  }, [query, placeId]);

  const { data, isLoading } = useQuery(
    ["getPlaceInspectors", currentPage, query, placeId],
    () => {
      return apiService.MakeGetRequest(
        `places/${placeId}/inspectors${toQuery({
          page: currentPage,
          limit: PAGE_SIZE,
          query,
        })}`,
        token,
      );
    },
    {
      enabled: !!placeId,
    },
  );

  const deleteinspectors = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(
      `inspectors/${id}?placeId=${placeId}`,
      token,
    );
  });

  const [inspectorsToBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  // const handleBulkDelete = () => {
  //   inspectorsToBeDeleted.ids.forEach((id: string) => {
  //     if (!id) return;
  //     deleteinspectors.mutate(id, {
  //       onError(error) {
  //         toastError(general.fr.message.operationFailed);
  //       },
  //       onSuccess() {
  //         toastSuccess(general.fr.message.inspectorDeleted);
  //         queryClient.invalidateQueries("getPlaceInspectors");
  //       },
  //     });
  //   });
  // };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        inspectorsToBeDeleted.ids
          .filter(Boolean)
          .map((id) => deleteinspectors.mutateAsync(id)),
      );

      toastSuccess(general.fr.message.inspectorDeleted);
      queryClient.invalidateQueries("getPlaceInspectors");
    } catch {
      toastError(general.fr.message.operationFailed);
    }
  };

  if (!placeId) {
    return (
      <div className="p-5 text-center">
        <h3>Aucun établissement sélectionné</h3>
        <p>Veuillez sélectionner un établissement</p>
      </div>
    );
  }

  return (
    <section>
      <h5 className="fs-4 fw-bold mb-2">Inspecteurs</h5>
      <BoxWrapper>
        <div className="d-flex justify-content-between align-items-baseline before-table">
          <div className="d-flex align-items-center gap-2 p-3 delete-add">
            <Link
              to={`/places/${placeId}/inspectors/new`}
              className="outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center shadow-secondary"
            >
              + Ajouter un inspecteur
            </Link>

            <button
              disabled={
                deleteinspectors.isLoading ||
                inspectorsToBeDeleted?.ids.length === 0
              }
              onClick={handleBulkDelete}
              className="outline-none btn btn-outline-danger rounded-pill text-danger d-flex align-items-center gap-2 justify-content-center shadow-primary"
            >
              {!deleteinspectors.isLoading ? (
                <>
                  <DeleteIcon fill={colors.primary} /> Supprimer
                </>
              ) : (
                <>
                  <CSpinner size="sm" /> {general.fr.wait}
                </>
              )}
            </button>
          </div>
          <div className="search-form">
            <div className=" input-field">
              <MagnifyingGlassIcon color="gray" width={20} />
              <input
                type="text"
                className="w-50 border-0 outline-none rounded-sm p-1 text-gray fs-6"
                placeholder="Rechercher"
                onChange={(e) =>
                  setSearchQuery(e?.target?.value?.toLowerCase())
                }
              />
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3"
            style={{ borderTopRightRadius: "8px", alignSelf: "flex-start" }}
          >
            <PlaceSVG />
            <p className="fs-5 fw-bold m-0 text-white text-pre">
              {data?.meta?.totalItems ?? 0}{" "}
              {(data?.meta?.totalItems ?? 0) > 1 ? "Inspecteurs" : "Inspecteur"}
            </p>
          </div>
        </div>

        <RenderTable
          loading={isLoading}
          render={() =>
            (data?.meta?.totalItems ?? 0) > 0 ? (
              <PlaceInspectorsTable
                placeId={selectedPlaceId ?? params.placeId}
                inspectors={data?.items as Inspector[]}
                inspectorsToBeDeleted={dispatch}
              />
            ) : (
              <p className="text-center fs-5 my-2">{general.fr.noData}</p>
            )
          }
        />
      </BoxWrapper>
      <Pagination
        currentPage={currentPage}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
        siblingCount={0}
        totalCount={data?.meta?.totalPages ?? 0}
        pageSize={PAGE_SIZE}
      />
    </section>
  );
}
